import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, CircularProgress, Divider, TextField, MenuItem, Typography,
} from '@mui/material';
import { updateStudentProfile } from '../studentPortalApi';
import { fieldSxFromTheme, getPortalTheme } from '../portalTheme';

const IS_OPEN_GEMARA = process.env.REACT_APP_EXAM_MODE === 'open-gemara';

const TYPE_LABELS = {
  time: 'זמן',
  shimur: 'שימור',
  miktzoa: 'מקצוע',
};

const kolelLabel = (opt) => {
  if (!opt) return '';
  return `${opt.name}${opt.city ? ` (${opt.city})` : ''}${opt.code != null ? ` — ${opt.code}` : ''}`;
};

const kolelFilterOpts = (opts, state) => {
  const q = state.inputValue.trim().toLowerCase();
  if (!q) return opts;
  return opts.filter(
    (o) =>
      o.name?.toLowerCase().includes(q) ||
      o.city?.toLowerCase().includes(q) ||
      String(o.code ?? '').includes(q)
  );
};

function GradesHistorySection({ periods = [], loading = false, theme }) {
  const t = theme || getPortalTheme(true);
  const hasGrades = periods.some((p) => (p.grades || []).length > 0);

  return (
    <Box sx={{ mt: 4, mb: 1 }}>
      <Typography
        sx={{
          color: '#f5f0e6',
          fontFamily: '"Frank Ruhl Libre", "David Libre", serif',
          fontWeight: 700,
          fontSize: '1.15rem',
          mb: 1,
          textAlign: 'center',
        }}
      >
        מבחנים קודמים
      </Typography>
      <Typography sx={{ color: 'rgba(245,240,230,0.7)', mb: 2, textAlign: 'center', fontSize: '0.9rem' }}>
        הציונים שלך ממועדים קודמים
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={22} sx={{ color: t.progressColor }} />
        </Box>
      ) : !hasGrades ? (
        <Typography sx={{ color: 'rgba(245,240,230,0.55)', textAlign: 'center', fontSize: '0.9rem', py: 1 }}>
          עדיין אין ציונים במערכת
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {periods.map((period) => (
            <Box
              key={period.id || period.name}
              sx={{
                border: `1px solid ${t.accentBorderSoft}`,
                borderRadius: 2,
                overflow: 'hidden',
                background: t.panelBg,
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 1.25,
                  borderBottom: `1px solid ${t.accentBorderSoft}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                }}
              >
                <Typography sx={{ color: t.accent, fontWeight: 700, fontSize: '0.95rem' }}>
                  {period.name}
                </Typography>
                {period.isActive && (
                  <Typography sx={{ color: 'rgba(245,240,230,0.55)', fontSize: '0.75rem' }}>
                    מועד נוכחי
                  </Typography>
                )}
              </Box>
              {(period.grades || []).map((g, i) => (
                <Box key={g.id}>
                  {i > 0 && <Divider sx={{ borderColor: t.accentBorderSoft }} />}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      justifyContent: 'space-between',
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 0.75, sm: 2 },
                      px: { xs: 2, sm: 2.5 },
                      py: 1.4,
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ color: '#f5f0e6', fontWeight: 600, fontSize: '0.92rem', wordBreak: 'break-word' }}>
                        {g.examName || g.examCode || 'מבחן'}
                        {g.examCode ? (
                          <Box component="span" sx={{ color: 'rgba(245,240,230,0.55)', fontWeight: 500 }}>
                            {' '}({g.examCode})
                          </Box>
                        ) : null}
                      </Typography>
                      {(TYPE_LABELS[g.examType] || g.examType) && (
                        <Typography sx={{ color: 'rgba(245,240,230,0.55)', fontSize: '0.8rem', mt: 0.25 }}>
                          {TYPE_LABELS[g.examType] || g.examType}
                        </Typography>
                      )}
                    </Box>
                    <Typography
                      sx={{
                        color: g.score >= 90 ? t.accent : '#f5f0e6',
                        fontWeight: 700,
                        fontSize: '1.1rem',
                        fontVariantNumeric: 'tabular-nums',
                        flexShrink: 0,
                      }}
                    >
                      {g.score}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

const DetailsStep = ({
  member,
  examCities,
  location,
  setLocation,
  onBack,
  onNext,
  editable = false,
  kolels = [],
  onMemberUpdated,
  systemLabel = 'מרא דתלמודא',
  theme,
  gradePeriods = [],
  gradesLoading = false,
  showGrades = false,
}) => {
  const t = theme || getPortalTheme(editable);
  const fieldSx = fieldSxFromTheme(t);
  const autocompleteSx = {
    ...fieldSx,
    '& .MuiAutocomplete-popupIndicator': { color: t.accentSoft },
    '& .MuiAutocomplete-clearIndicator': { color: 'rgba(245,240,230,0.6)' },
  };
  const autocompleteListSx = {
    bgcolor: t.listBg,
    color: '#f5f0e6',
    '& .MuiAutocomplete-option': {
      '&[aria-selected="true"]': { bgcolor: t.accentMuted.replace('0.35', '0.2').replace('0.38', '0.2') },
      '&.Mui-focused': { bgcolor: t.accentMuted.replace('0.35', '0.12').replace('0.38', '0.12') },
    },
  };

  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    fatherName: '',
    fatherInLawName: '',
    phone: '',
    city: '',
    street: '',
    buildingNumber: '',
    apartment: '',
    entrance: '',
  });
  const [kolelMorning, setKolelMorning] = useState(null);
  const [kolelAfternoon, setKolelAfternoon] = useState(null);
  const [kolelEvening, setKolelEvening] = useState(null);

  const kolelById = useMemo(() => {
    const map = {};
    for (const k of kolels || []) map[String(k._id)] = k;
    return map;
  }, [kolels]);

  useEffect(() => {
    if (!member) return;
    setForm({
      firstName: member.firstName || '',
      lastName: member.lastName || '',
      fatherName: member.fatherName || '',
      fatherInLawName: member.fatherInLawName || '',
      phone: member.phone || '',
      city: member.city || '',
      street: member.street || '',
      buildingNumber: member.buildingNumber || '',
      apartment: member.apartment || '',
      entrance: member.entrance || '',
    });
    if (IS_OPEN_GEMARA) {
      const primaryRaw = member.kolelRef || member.kolelMorningRef || null;
      const primaryId = primaryRaw?._id || primaryRaw || null;
      setKolelMorning(primaryId ? kolelById[String(primaryId)] || null : null);
      setKolelAfternoon(null);
      setKolelEvening(null);
    } else {
      setKolelMorning(member.kolelMorningRef ? kolelById[member.kolelMorningRef] || null : null);
      setKolelAfternoon(member.kolelAfternoonRef ? kolelById[member.kolelAfternoonRef] || null : null);
      setKolelEvening(member.kolelEveningRef ? kolelById[member.kolelEveningRef] || null : null);
    }
  }, [member, kolelById]);

  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const rows = [
    { label: 'שם', value: `${member?.firstName || ''} ${member?.lastName || ''}`.trim() || '—' },
    { label: 'שם האב', value: member?.fatherName || '—' },
    { label: 'עיר מגורים', value: member?.city || '—' },
    { label: 'כולל', value: member?.kolel || '—' },
    { label: 'טלפון', value: member?.phone || '—' },
    { label: 'קוד אישי', value: member?.personalCode ?? '—' },
  ];

  const handleContinueReadonly = () => {
    setError('');
    if (!location) {
      setError('בחירת מוקד בחינה היא חובה');
      return;
    }
    onNext();
  };

  const handleSaveAndContinue = async () => {
    setError('');
    if (!form.firstName.trim() || !form.lastName.trim()) {
      setError('שם פרטי ושם משפחה נדרשים');
      return;
    }
    if (!form.fatherName.trim()) {
      setError('שם האב נדרש');
      return;
    }
    if (!form.city.trim()) {
      setError('עיר מגורים נדרשת');
      return;
    }
    if (!form.phone.trim()) {
      setError('מספר טלפון נדרש');
      return;
    }
    if (!location) {
      setError('בחירת מוקד בחינה היא חובה');
      return;
    }

    setSaving(true);
    try {
      const data = await updateStudentProfile({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        fatherName: form.fatherName.trim(),
        fatherInLawName: form.fatherInLawName.trim(),
        phone: form.phone.trim(),
        city: form.city.trim(),
        street: form.street.trim(),
        buildingNumber: form.buildingNumber.trim(),
        apartment: form.apartment.trim(),
        entrance: form.entrance.trim(),
        kolelMorningRef: kolelMorning?._id || null,
        kolelAfternoonRef: IS_OPEN_GEMARA ? null : (kolelAfternoon?._id || null),
        kolelEveningRef: IS_OPEN_GEMARA ? null : (kolelEvening?._id || null),
      });
      onMemberUpdated?.(data.member);
      onNext();
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה בשמירת הפרטים');
    } finally {
      setSaving(false);
    }
  };

  const renderKolelSelect = (label, value, onChange) => (
    <Autocomplete
      options={kolels || []}
      getOptionLabel={kolelLabel}
      value={value}
      onChange={(_, val) => onChange(val)}
      filterOptions={kolelFilterOpts}
      isOptionEqualToValue={(opt, val) => opt._id === val?._id}
      clearOnEscape
      noOptionsText="לא נמצא כולל"
      sx={{ mt: 2, ...autocompleteSx }}
      ListboxProps={{ sx: autocompleteListSx }}
      renderInput={(params) => (
        <TextField {...params} label={label} placeholder="חיפוש לפי שם, עיר או קוד..." sx={fieldSx} />
      )}
    />
  );

  if (editable) {
    return (
      <Box sx={{ maxWidth: 520, mx: 'auto' }}>
        <Typography
          variant="h5"
          sx={{
            color: '#f5f0e6',
            fontFamily: '"Frank Ruhl Libre", "David Libre", serif',
            fontWeight: 700,
            mb: 1,
            textAlign: 'center',
          }}
        >
          פרטים אישיים ומוקד
        </Typography>
        <Typography sx={{ color: 'rgba(245,240,230,0.75)', mb: 3, textAlign: 'center', fontSize: '0.95rem', lineHeight: 1.8 }}>
          עדכנו את הפרטים במידת הצורך, בחרו מוקד בחינה ולחצו שמירה והמשך.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

        <TextField fullWidth required label="שם פרטי" value={form.firstName} onChange={setField('firstName')} margin="normal" sx={fieldSx} />
        <TextField fullWidth required label="שם משפחה" value={form.lastName} onChange={setField('lastName')} margin="normal" sx={fieldSx} />
        <TextField fullWidth required label="שם האב" value={form.fatherName} onChange={setField('fatherName')} margin="normal" sx={fieldSx} />
        <TextField fullWidth label="שם חותן (לא חובה)" value={form.fatherInLawName} onChange={setField('fatherInLawName')} margin="normal" sx={fieldSx} />
        <TextField fullWidth required label="מספר טלפון" value={form.phone} onChange={setField('phone')} margin="normal" inputProps={{ inputMode: 'tel', dir: 'ltr' }} sx={fieldSx} />
        <TextField fullWidth required label="עיר מגורים" value={form.city} onChange={setField('city')} margin="normal" sx={fieldSx} />
        <TextField fullWidth label="רחוב" value={form.street} onChange={setField('street')} margin="normal" sx={fieldSx} />
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 0, sm: 1.5 } }}>
          <TextField fullWidth label="מספר בניין" value={form.buildingNumber} onChange={setField('buildingNumber')} margin="normal" sx={fieldSx} />
          <TextField fullWidth label="דירה" value={form.apartment} onChange={setField('apartment')} margin="normal" sx={fieldSx} />
          <TextField fullWidth label="כניסה" value={form.entrance} onChange={setField('entrance')} margin="normal" sx={fieldSx} />
        </Box>

        {IS_OPEN_GEMARA ? (
          renderKolelSelect('כולל', kolelMorning, setKolelMorning)
        ) : (
          <>
            {renderKolelSelect('כולל בוקר', kolelMorning, setKolelMorning)}
            {renderKolelSelect('כולל צהריים', kolelAfternoon, setKolelAfternoon)}
            {renderKolelSelect('כולל ערב', kolelEvening, setKolelEvening)}
          </>
        )}

        <TextField
          fullWidth
          label="קוד אישי"
          value={member?.personalCode ?? ''}
          margin="normal"
          disabled
          inputProps={{ dir: 'ltr', style: { textAlign: 'center', letterSpacing: '0.12em' } }}
          sx={{
            ...fieldSx,
            '& .MuiInputLabel-root.Mui-disabled': {
              color: t.accentSoft,
              opacity: 1,
            },
            '& .MuiOutlinedInput-root.Mui-disabled': {
              color: t.accent,
              WebkitTextFillColor: t.accent,
              opacity: 1,
              background: 'rgba(255,255,255,0.08)',
              '& fieldset': { borderColor: t.accentBorder },
              '& input': {
                WebkitTextFillColor: t.accent,
                color: t.accent,
                opacity: 1,
                fontWeight: 800,
                fontSize: '1.2rem',
                fontFamily: 'ui-monospace, "Cascadia Code", "Segoe UI Mono", Consolas, monospace',
              },
            },
          }}
        />

        <TextField
          select
          fullWidth
          required
          label="מוקד בחינה — המקום שבו תיבחנו"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          margin="normal"
          sx={fieldSx}
        >
          <MenuItem value=""><em>בחרו מוקד...</em></MenuItem>
          {(examCities || []).map((city) => (
            <MenuItem key={city} value={city}>{city}</MenuItem>
          ))}
        </TextField>

        {showGrades && (
          <GradesHistorySection periods={gradePeriods} loading={gradesLoading} theme={t} />
        )}

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: 1.5, mt: 3 }}>
          <Button
            onClick={onBack}
            disabled={saving}
            sx={{
              flex: 1,
              minHeight: 48,
              py: 1.2,
              borderRadius: 2,
              color: 'rgba(245,240,230,0.85)',
              border: `1px solid ${t.accentBorder}`,
            }}
          >
            חזרה
          </Button>
          <Button
            onClick={handleSaveAndContinue}
            disabled={saving}
            sx={{
              flex: 2,
              minHeight: 48,
              py: 1.2,
              fontWeight: 700,
              borderRadius: 2,
              color: t.ctaColor,
              background: t.ctaBg,
              '&:hover': { filter: 'brightness(1.06)' },
            }}
          >
            {saving ? <CircularProgress size={22} sx={{ color: t.ctaColor }} /> : 'שמירה והמשך'}
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 520, mx: 'auto' }}>
      <Typography
        variant="h5"
        sx={{
          color: '#f5f0e6',
          fontFamily: '"Frank Ruhl Libre", "David Libre", serif',
          fontWeight: 700,
          mb: 1,
          textAlign: 'center',
        }}
      >
        אישור הפרטים
      </Typography>
      <Typography sx={{ color: 'rgba(245,240,230,0.75)', mb: 3, textAlign: 'center', fontSize: '0.95rem', lineHeight: 1.8 }}>
        בדקו שהפרטים נכונים. אם הפרטים לא נכונים — נא להתקשר למערכת {systemLabel}{' '}
        <Box component="span" dir="ltr" sx={{ color: t.accent, fontWeight: 700, whiteSpace: 'nowrap' }}>
          077-4015292
        </Box>
        {' '}שלוחה 5 ← 5 לתקן את הפרטים.
        <br />
        בחרו מוקד בחינה ולחצו המשך.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

      <Box
        sx={{
          border: `1px solid ${t.accentBorder}`,
          borderRadius: 2,
          overflow: 'hidden',
          mb: 3,
          background: t.panelBg,
        }}
      >
        {rows.map((row, i) => {
          const isPersonalCode = row.label === 'קוד אישי';
          return (
          <Box key={row.label}>
            {i > 0 && <Divider sx={{ borderColor: t.accentBorderSoft }} />}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                gap: { xs: 0.5, sm: 2 },
                px: { xs: 2, sm: 2.5 },
                py: 1.5,
              }}
            >
              <Typography sx={{ color: 'rgba(245,240,230,0.65)', fontSize: '0.9rem', flexShrink: 0 }}>{row.label}</Typography>
              <Typography
                dir={isPersonalCode ? 'ltr' : undefined}
                sx={{
                  color: isPersonalCode ? t.accent : '#f5f0e6',
                  fontWeight: isPersonalCode ? 800 : 600,
                  fontSize: isPersonalCode ? '1.15rem' : '0.95rem',
                  letterSpacing: isPersonalCode ? '0.12em' : undefined,
                  fontFamily: isPersonalCode
                    ? 'ui-monospace, "Cascadia Code", "Segoe UI Mono", Consolas, monospace'
                    : undefined,
                  wordBreak: 'break-word',
                  textAlign: { xs: 'start', sm: 'end' },
                }}
              >
                {row.value}
              </Typography>
            </Box>
          </Box>
          );
        })}
      </Box>

      <TextField
        select
        fullWidth
        required
        label="מוקד בחינה — המקום שבו תיבחנו"
        value={location}
        onChange={(e) => setLocation(e.target.value)}
        margin="normal"
        sx={fieldSx}
      >
        <MenuItem value="">
          <em>בחרו מוקד...</em>
        </MenuItem>
        {(examCities || []).map((city) => (
          <MenuItem key={city} value={city}>{city}</MenuItem>
        ))}
      </TextField>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: 1.5, mt: 3 }}>
        <Button
          onClick={onBack}
          sx={{
            flex: 1,
            minHeight: 48,
            py: 1.2,
            borderRadius: 2,
            color: 'rgba(245,240,230,0.85)',
            border: `1px solid ${t.accentBorder}`,
          }}
        >
          חזרה
        </Button>
        <Button
          onClick={handleContinueReadonly}
          sx={{
            flex: 2,
            minHeight: 48,
            py: 1.2,
            fontWeight: 700,
            borderRadius: 2,
            color: t.ctaColor,
            background: t.ctaBg,
            '&:hover': { filter: 'brightness(1.06)' },
          }}
        >
          המשך
        </Button>
      </Box>
    </Box>
  );
};

export default DetailsStep;
